import { MapPin, Wrench, MonitorSmartphone } from "lucide-react";
import { todayStr } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field } from "./common";
import { FrDateInput } from "./FrDateInput";
import { ACCESSORIES, MODES } from "@/lib/constants";

export const MODE_ICONS = { site: MapPin, atelier: Wrench, distance: MonitorSmartphone };
export const modeOf = (i) => i?.mode || (i?.type === "assistance_distance" ? "distance" : "site");
const val = (e) => (e?.target ? e.target.value : e);

export const ModeIcon = ({ item, className = "h-3.5 w-3.5" }) => {
  const m = modeOf(item);
  const Icon = MODE_ICONS[m];
  return <Icon className={`inline shrink-0 text-slate-400 ${className}`} aria-label={MODES[m]} data-testid={`mode-icon-${m}`}><title>{MODES[m]}</title></Icon>;
};

export const ModeSelector = ({ value, onChange, testid = "mode" }) => (
  <div className="grid grid-cols-3 gap-2" data-testid={`${testid}-selector`}>
    {Object.entries(MODES).map(([k, label]) => {
      const Icon = MODE_ICONS[k];
      const on = value === k;
      return (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`} aria-pressed={on}
          className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border px-2 text-sm transition-colors ${on ? "border-cyan-400 bg-cyan-400/15 text-cyan-100" : "border-white/10 text-slate-300 hover:border-cyan-400/40"}`}>
          <Icon className="h-5 w-5" />{label}
        </button>
      );
    })}
  </div>
);

const AccessoryChips = ({ list, onChange }) => {
  const toggle = (a) => onChange(list.includes(a) ? list.filter((x) => x !== a) : [...list, a]);
  const extra = list.filter((a) => !ACCESSORIES.includes(a)).join(", ");
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {ACCESSORIES.map((a) => (
          <button key={a} type="button" onClick={() => toggle(a)} data-testid={`accessory-${a}`}
            className={`rounded-full border px-3 py-1.5 text-sm ${list.includes(a) ? "border-cyan-400 bg-cyan-400/15 text-cyan-100" : "border-white/10 text-slate-400"}`}>{a}</button>
        ))}
      </div>
      <Input defaultValue={extra} placeholder="Autres (séparés par des virgules)" data-testid="accessory-other-input"
        onBlur={(e) => onChange([...list.filter((a) => ACCESSORIES.includes(a)), ...e.target.value.split(",").map((x) => x.trim()).filter(Boolean)])} />
    </div>
  );
};

export const WorkshopFields = ({ f, set }) => {
  const ws = f.workshop || {};
  const up = (k) => (e) => set("workshop")({ ...ws, [k]: val(e) || (k === "accessories" ? [] : null) });
  return (
    <div className="grid gap-4 rounded-xl border border-cyan-400/15 bg-white/[0.02] p-3 sm:col-span-2 sm:grid-cols-2" data-testid="workshop-fields">
      <Field label="Date de dépôt"><FrDateInput type="date" value={ws.deposit_date || ""} onChange={up("deposit_date")} data-testid="workshop-deposit-date" /></Field>
      <Field label="Disponibilité prévue"><FrDateInput type="date" value={ws.expected_date || ""} onChange={up("expected_date")} data-testid="workshop-expected-date" /></Field>
      <Field label="Matériel et accessoires confiés" className="sm:col-span-2"><AccessoryChips list={ws.accessories || []} onChange={(l) => set("workshop")({ ...ws, accessories: l })} /></Field>
      <Field label="État du matériel à la réception" className="sm:col-span-2">
        <Textarea value={ws.condition || ""} onChange={(e) => set("workshop")({ ...ws, condition: e.target.value })} placeholder="Rayures, écran fissuré, démarre / ne démarre pas…" data-testid="workshop-condition-input" />
      </Field>
      <label className="flex items-start gap-3 text-sm text-slate-200 sm:col-span-2">
        <Switch checked={!!f.share_pickup_address} onCheckedChange={set("share_pickup_address")} data-testid="workshop-share-address-switch" />
        <span>Communiquer l'adresse de dépôt dans l'e-mail de rendez-vous<span className="block text-xs text-slate-400">Désactivé par défaut : votre adresse n'est jamais envoyée sans votre accord.</span></span>
      </label>
    </div>
  );
};

export const ModeBlock = ({ f, set, testid }) => {
  const mode = modeOf(f);
  const choose = (m) => {
    set("mode")(m);
    if (m === "atelier" && !f.id) set("notify_client")(false);
    if (m === "atelier" && !f.workshop?.deposit_date) set("workshop")({ ...(f.workshop || {}), deposit_date: todayStr() });
  };
  return (
    <>
      <Field label="Mode d'intervention" className="sm:col-span-2"><ModeSelector value={mode} onChange={choose} testid={testid} /></Field>
      {mode === "atelier" && <WorkshopFields f={f} set={set} />}
    </>
  );
};
